import type { Disposition, RejectedRow, SendOutcome } from './types'

/** Statuses that mean "the server is having a moment", not "this is wrong". */
const RETRYABLE_STATUSES = new Set([408, 429])

/**
 * Decide what a send attempt means for the queued work.
 *
 * This is the safety-critical function of the whole feature: misclassify a
 * transient failure as permanent and an operator's scan is silently discarded;
 * misclassify a permanent one as transient and the queue retries forever while
 * nobody is told. Pure, so every branch is testable.
 */
export function classifyOutcome(outcome: SendOutcome): Disposition {
  const { status, body } = outcome

  // No answer at all: offline, DNS, timeout, connection reset mid-flight.
  if (status == null) {
    return { kind: 'retry', reason: outcome.message || 'No response from the server' }
  }

  if (status === 401) {
    return { kind: 'auth', reason: 'Session expired' }
  }

  if (status >= 500 || RETRYABLE_STATUSES.has(status)) {
    return { kind: 'retry', reason: describe(body) || `Server returned ${status}` }
  }

  if (status >= 200 && status < 300) {
    const rejected = readRejected(body)
    return rejected.length > 0 ? { kind: 'partial', rejected } : { kind: 'accepted' }
  }

  return { kind: 'permanent', reason: describe(body) || `Server refused the batch (${status})` }
}

/** Pull ID14's canonical `rejected[]` out of a `/sync` envelope, if present. */
function readRejected(body: unknown): RejectedRow[] {
  if (!body || typeof body !== 'object') return []
  const rejected = (body as { rejected?: unknown }).rejected
  return Array.isArray(rejected) ? (rejected as RejectedRow[]) : []
}

/** Best human-readable line out of a ProblemDetails-ish body. */
function describe(body: unknown): string | null {
  if (!body) return null
  if (typeof body === 'string') return body.trim() || null
  if (typeof body === 'object') {
    const candidate = body as { title?: unknown; detail?: unknown; message?: unknown }
    for (const value of [candidate.detail, candidate.title, candidate.message]) {
      if (typeof value === 'string' && value.trim()) return value.trim()
    }
  }
  return null
}
